import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Request interceptor to add auth token and fix multipart uploads
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and we haven't already retried, try refreshing the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(
            `${API_BASE_URL}/users/refresh`,
            { refreshToken }
          );
          
          if (response.data?.success && response.data?.token) {
            // Save new tokens
            localStorage.setItem('token', response.data.token);
            if (response.data.refreshToken) {
              localStorage.setItem('refreshToken', response.data.refreshToken);
            }
            
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // Refresh failed — clear tokens and redirect
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          const publicPaths = ['/', '/login', '/register', '/forgot-password'];
          const isPublicPage = publicPaths.some(p => window.location.pathname === p);
          if (!isPublicPage) {
            window.location.replace('/login');
          }
          return Promise.reject(refreshError);
        }
      }
      
      // No refresh token available — clear and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      const publicPaths = ['/', '/login', '/register', '/forgot-password'];
      const isPublicPage = publicPaths.some(p => window.location.pathname === p);
      if (!isPublicPage) {
        window.location.replace('/login');
      }
    }
    
    // Handle network errors
    if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
      console.error('Backend server connection failed. Make sure the backend is running on port 5010.');
    }
    
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  // Login user
  login: (credentials: { email?: string; username?: string; password: string }) =>
    api.post('/users/signin', credentials),

  // Register user
  register: (userData: { name: string; email: string; password: string; role: string }) =>
    api.post('/users/signup', userData),

  // Get user profile
  getProfile: () =>
    api.get('/users/profile'),

  // Update user profile
  updateProfile: (userData: any) =>
    api.put('/users/profile', userData),

  // Refresh token
  refreshToken: (refreshToken: string) =>
    api.post('/users/refresh', { refreshToken }),

  // Verify token
  verifyToken: () =>
    api.get('/users/verify'),

  // Forgot password - send verification code
  forgotPassword: (email: string) =>
    api.post('/users/forgot-password', { email }),

  // Reset password with verification code
  resetPassword: (data: { email: string; code: string; newPassword: string }) =>
    api.post('/users/reset-password', data),
};

// Contact API (public)
export const contactAPI = {
  sendMessage: (data: { name: string; email: string; message: string }) =>
    api.post('/contact', data),
};

// Admin API
export const adminAPI = {
  // Get admin stats
  getStats: () =>
    api.get('/admin/stats'),

  // Get all users
  getUsers: (params = {}) =>
    api.get('/admin/users', { params }),

  // Get user by ID
  getUser: (id: string) =>
    api.get(`/admin/users/${id}`),

  // Update user
  updateUser: (id: string, userData: any) =>
    api.put(`/admin/users/${id}`, userData),

  // Delete user
  deleteUser: (id: string) =>
    api.delete(`/admin/users/${id}`),

  // Create user
  createUser: (userData: any) =>
    api.post('/admin/users', userData),

  // All Settings Management
  getAllSettings: () =>
    api.get('/admin/elasticsearch/all-settings'),

  updateAllSettings: (settings: any) =>
    api.put('/admin/elasticsearch/all-settings', { settings }),

  resetAllSettings: () =>
    api.post('/admin/elasticsearch/all-settings/reset'),

  // System Settings Management
  getSystemSettings: () =>
    api.get('/admin/elasticsearch/system-settings'),

  // Public System Settings (for guest users)
  getPublicSystemSettings: () =>
    api.get('/public/system-settings'),

  updateSystemSettings: (settings: any) =>
    api.put('/admin/elasticsearch/system-settings', { settings }),

  // Import Settings Management
  getImportSettings: () =>
    api.get('/admin/elasticsearch/import-settings'),

  updateImportSettings: (settings: any) =>
    api.put('/admin/elasticsearch/import-settings', { settings }),

  resetImportSettings: () =>
    api.post('/admin/elasticsearch/import-settings/reset'),

  // Elasticsearch Admin API
  getElasticsearchHealth: () =>
    api.get('/admin/elasticsearch/health'),

  getElasticsearchIndices: () =>
    api.get('/admin/elasticsearch/indices'),

  createElasticsearchIndex: (indexName: string, mapping?: any, metadata?: any) =>
    api.post('/admin/elasticsearch/indices', { indexName, mapping, metadata }),

  createPredefinedIndices: () =>
    api.post('/admin/elasticsearch/indices/predefined'),

  deleteElasticsearchIndex: (indexName: string) =>
    api.delete(`/admin/elasticsearch/indices/${indexName}`),

  // Index metadata management
  getIndexMetadata: (indexName: string) =>
    api.get(`/admin/elasticsearch/indices/${indexName}/metadata`),

  updateIndexMetadata: (indexName: string, metadata: any) =>
    api.put(`/admin/elasticsearch/indices/${indexName}/metadata`, metadata),

  deleteIndexMetadata: (indexName: string) =>
    api.delete(`/admin/elasticsearch/indices/${indexName}/metadata`),

  // Re-configure filter fields (deduplicate + rebuild)
  reconfigureIndex: (indexName: string) =>
    api.post(`/admin/elasticsearch/indices/${indexName}/re-configure`),

  importElasticsearchData: (indexName: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/elasticsearch/indices/${indexName}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  importElasticsearchDataWithProgress: async (indexName: string, file: File, bulkSize?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (bulkSize) {
      formData.append('bulkSize', bulkSize.toString());
    }
    
    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    console.log('Using token for SSE:', token ? 'Token found' : 'No token');
    
    // Use fetch for Server-Sent Events instead of axios
    const response = await fetch(`/api/admin/elasticsearch/indices/${indexName}/import-with-progress`, {
      method: 'POST',
      body: formData,
      credentials: 'include', // Include cookies for authentication
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });
    
    console.log('SSE Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('SSE Error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    return response;
  },

  importElasticsearchLargeFile: async (indexName: string, file: File, bulkSize?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (bulkSize) {
      formData.append('bulkSize', bulkSize.toString());
    }
    
    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    console.log('Using token for large file SSE:', token ? 'Token found' : 'No token');
    
    // Use fetch for Server-Sent Events for large files
    const response = await fetch(`/api/admin/elasticsearch/indices/${indexName}/import-large-file`, {
      method: 'POST',
      body: formData,
      credentials: 'include', // Include cookies for authentication
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });
    
    console.log('Large file SSE Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Large file SSE Error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    return response;
  },

  getElasticsearchMappings: (indexName: string) =>
    api.get(`/admin/elasticsearch/indices/${indexName}/mapping`),

  getElasticsearchStats: (indexName?: string) =>
    api.get(`/admin/elasticsearch/indices/${indexName}/stats`),

  // New methods for the wizard
  getSupportedFormats: () =>
    api.get('/admin/elasticsearch/supported-formats'),

  getIndexCompatibility: (indexName: string) =>
    api.get(`/admin/elasticsearch/indices/${indexName}/schema`),

  // Data Management API
  getDocuments: (indexName: string, params?: {
    page?: number;
    size?: number;
    search?: string;
    sortField?: string;
    sortOrder?: 'asc' | 'desc';
    fields?: string;
  }) =>
    api.get(`/admin/elasticsearch/indices/${indexName}/documents`, { params }),

  getDocument: (indexName: string, documentId: string) =>
    api.get(`/admin/elasticsearch/indices/${indexName}/documents/${documentId}`),

  createDocument: (indexName: string, document: any, documentId?: string) =>
    api.post(`/admin/elasticsearch/indices/${indexName}/documents`, {
      document,
      documentId
    }),

  updateDocument: (indexName: string, documentId: string, document: any) =>
    api.put(`/admin/elasticsearch/indices/${indexName}/documents/${documentId}`, {
      document
    }),

  deleteDocument: (indexName: string, documentId: string) =>
    api.delete(`/admin/elasticsearch/indices/${indexName}/documents/${documentId}`),

  bulkDocumentOperations: (indexName: string, operations: Array<{
    action: 'index' | 'create' | 'delete';
    document?: any;
    documentId: string;
  }>) =>
    api.post(`/admin/elasticsearch/indices/${indexName}/documents/bulk`, {
      operations
    }),

  getSortFields: (indexName: string) =>
    api.get(`/admin/elasticsearch/indices/${indexName}/sort-fields`),

  getIndexSchema: (indexName: string) =>
    api.get(`/admin/elasticsearch/indices/${indexName}/schema`),

  // Filter Values API
  getFilterValues: (indexName: string) =>
    api.get(`/admin/elasticsearch/indices/${indexName}/filter-values`),
  
  getFieldValues: (indexName: string, fieldName: string) =>
    api.get(`/admin/elasticsearch/indices/${indexName}/filter-values/${fieldName}`),
  
  clearFilterValues: (indexName: string) =>
    api.delete(`/admin/elasticsearch/indices/${indexName}/filter-values`),
};

// Dynamic Index Management API
export const agriculturalAPI = {
  // Index-based search API
  getIndices: () => 
    api.get('/agricultural/indices'),

  getIndexFields: (indexName: string) => 
    api.get(`/agricultural/indices/${indexName}/fields`),

  getIndexFilterValues: (indexName: string, fieldName: string, size = 1000) => 
    api.get(`/agricultural/indices/${indexName}/filter-values/${fieldName}`, { params: { size } }),

  getAllIndexFilterValues: (indexName: string, size = 1000) => 
    api.get(`/agricultural/indices/${indexName}/filter-values`, { params: { size } }),

  searchIndex: (indexName: string, params = {}) => 
    api.get(`/agricultural/indices/${indexName}/search`, { params }),

  getSearchSuggestions: (indexName: string, query: string, limit = 10) => 
    api.get(`/agricultural/indices/${indexName}/suggestions`, { params: { q: query, limit } }),

  // Dynamic analytics for specific index
  getIndexAnalytics: (indexName: string, params = {}) => 
    api.get(`/agricultural/indices/${indexName}/analytics`, { params }),

  // Terms aggregation with filters
  getIndexTerms: (indexName: string, body: { field: string; size?: number; filters?: any }) =>
    api.post(`/agricultural/indices/${indexName}/terms`, body),

  // Server-side chart aggregation over the full index (not limited to fetched docs)
  aggregateIndex: (indexName: string, body: {
    xField: string;
    yField?: string;
    seriesFields?: string[];
    aggregation?: 'sum' | 'avg' | 'min' | 'max' | 'count';
    filters?: any;
    limit?: number;
    seriesLimit?: number;
  }) =>
    api.post(`/agricultural/indices/${indexName}/aggregate`, body),

  // Auto-configure filter fields for an index
  autoConfigureIndex: (indexName: string) =>
    api.post(`/agricultural/indices/${indexName}/auto-configure`),

  // Legacy analytics endpoint (deprecated)
  getAnalytics: (params = {}) => 
    api.get('/agricultural/analytics', { params }),
};

// Charts API - NEW
export const chartsAPI = {
  // Get all my charts
  getCharts: (includePublic = false) =>
    api.get('/charts', { params: { includePublic } }),

  
  // Get chart by ID
  getChart: (id: string) =>
    api.get(`/charts/${id}`),

  // Create chart
  createChart: (chartData: any) =>
    api.post('/charts', chartData),

  // Create multiple charts in batch
  createMultipleCharts: (chartsData: { charts: any[]; baseName: string; description?: string; tags?: string[]; isPublic?: boolean }) =>
    api.post('/charts/batch', chartsData),

  // Update chart
  updateChart: (id: string, chartData: any) =>
    api.put(`/charts/${id}`, chartData),

  // Delete chart
  deleteChart: (id: string) =>
    api.delete(`/charts/${id}`),

  // Get chart data (generated)
  getChartData: (id: string) =>
    api.get(`/charts/${id}/data`),

  // Get fields for index (for builder)
  getIndexFieldsForBuilder: (indexName: string) =>
    api.get(`/charts/builder/indexes/${indexName}/fields`),

  // Search public charts
  searchPublicCharts: (params = {}) =>
    api.get('/charts/public', { params }),

  // Duplicate chart
  duplicateChart: (id: string, newName?: string) =>
    api.post(`/charts/${id}/duplicate`, { name: newName }),
};
export const businessAPI = {
  // Get all businesses with filters
  getBusinesses: (params = {}) =>
    api.get('/businesses', { params }),

  // Get business by ID
  getBusiness: (id: string) =>
    api.get(`/businesses/${id}`),

  // Get business by slug
  getBusinessBySlug: (slug: string) =>
    api.get(`/businesses/slug/${slug}`),

  // Create business (authenticated)
  createBusiness: (businessData: any) =>
    api.post('/businesses', businessData),

  // Get current user's business (authenticated)
  getMyBusiness: () =>
    api.get('/businesses/my-business'),

  // Update business
  updateBusiness: (id: string, businessData: any) =>
    api.put(`/businesses/${id}`, businessData),

  // Delete business
  deleteBusiness: (id: string) =>
    api.delete(`/businesses/${id}`),

  // Rate business
  rateBusiness: (id: string, rating: number) =>
    api.post(`/businesses/${id}/rate`, { rating }),

  // Delete rating
  deleteRating: (id: string) =>
    api.delete(`/businesses/${id}/rate`),

  // Get categories
  getCategories: () =>
    api.get('/businesses/categories/list'),

  // Get industries
  getIndustries: () =>
    api.get('/businesses/industries/list'),
};

// Forum API
export const forumAPI = {
  getPosts: (params = {}) =>
    api.get('/forum', { params }),

  getPost: (id: string) =>
    api.get(`/forum/${id}`),

  createPost: (postData: any) =>
    api.post('/forum', postData),

  updatePost: (id: string, postData: any) =>
    api.put(`/forum/${id}`, postData),

  deletePost: (id: string) =>
    api.delete(`/forum/${id}`),

  likePost: (id: string) =>
    api.post(`/forum/${id}/like`),

  addComment: (id: string, content: string) =>
    api.post(`/forum/${id}/comment`, { content }),

  deleteComment: (postId: string, commentId: string) =>
    api.delete(`/forum/${postId}/comment/${commentId}`),

  sharePost: (id: string) =>
    api.post(`/forum/${id}/share`),

  getUserPosts: (userId: string, params = {}) =>
    api.get(`/forum/user/${userId}`, { params }),

  getCategories: () =>
    api.get('/forum/categories/list'),

  createCategory: (categoryData: FormData | { name: string; description?: string }) =>
    api.post('/forum/categories', categoryData),

  deleteCategory: (name: string) =>
    api.delete(`/forum/categories/${encodeURIComponent(name)}`),
};

// News API (community news + agriculture/Google News)
export const newsAPI = {
  getCommunityNews: (params = {}) =>
    api.get('/news/community', { params }),
  getAgricultureNews: (params = {}) =>
    api.get('/news/agriculture', { params }),
  createCommunityNews: (data: FormData) =>
    api.post('/news/community', data),
  getCommunityNewsById: (id: string) =>
    api.get(`/news/community/${id}`),
  getCommunityNewsView: (id: string) =>
    api.get(`/news/community/view/${id}`),
  updateCommunityNews: (id: string, data: FormData) =>
    api.put(`/news/community/${id}`, data),
  deleteCommunityNews: (id: string) =>
    api.delete(`/news/community/${id}`),
  getFeaturedNews: (params = {}) =>
    api.get('/news/featured', { params }),
  likeNews: (id: string) =>
    api.post(`/news/community/${id}/like`),
  addComment: (id: string, content: string) =>
    api.post(`/news/community/${id}/comment`, { content }),
  deleteComment: (newsId: string, commentId: string) =>
    api.delete(`/news/community/${newsId}/comment/${commentId}`),
  recordShare: (id: string) =>
    api.post(`/news/community/${id}/share`),
  getArticleEngagement: (urls: string[]) =>
    api.post('/news/articles/engagement', { urls }),
  likeArticle: (url: string) =>
    api.post('/news/articles/like', { url }),
  addArticleComment: (url: string, content: string) =>
    api.post('/news/articles/comment', { url, content }),
  deleteArticleComment: (url: string, commentId: string) =>
    api.delete(`/news/articles/comment`, { params: { url, commentId } }),
  recordArticleShare: (url: string) =>
    api.post('/news/articles/share', { url }),
  subscribeNewsletter: (data: { email: string; name?: string }) =>
    api.post('/news/newsletter/subscribe', data),
};

// Courses API
export const coursesAPI = {
  getCourses: (filters?: any) => api.get('/courses', { params: filters }),
  getCourse: (id: string) => api.get(`/courses/${id}`),
  createCourse: (data: any) => api.post('/courses', data),
  uploadCourseImage: (formData: FormData) =>
    api.post('/courses/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadCourseVideo: (formData: FormData) =>
    api.post('/courses/upload-video', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadCourseSubtitle: (formData: FormData) =>
    api.post('/courses/upload-subtitle', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateCourse: (id: string, data: any) => api.put(`/courses/${id}`, data),
  deleteCourse: (id: string) => api.delete(`/courses/${id}`),
  enrollInCourse: (id: string) => api.post(`/courses/${id}/enroll`),
  getMyEnrollments: () => api.get('/courses/my/enrollments'),
  updateProgress: (courseId: string, lessonIndex: number) =>
    api.put(`/courses/${courseId}/lessons/${lessonIndex}/progress`),
  submitQuiz: (courseId: string, lessonIndex: number, answers: number[]) =>
    api.post(`/courses/${courseId}/lessons/${lessonIndex}/quiz`, { answers }),
  getForumPosts: (courseId: string, params?: any) =>
    api.get(`/courses/${courseId}/forum`, { params }),
  createForumPost: (courseId: string, data: any) =>
    api.post(`/courses/${courseId}/forum`, data),
  replyToPost: (courseId: string, postId: string, content: string) =>
    api.post(`/courses/${courseId}/forum/${postId}/reply`, { content }),
  toggleLike: (courseId: string, postId: string) =>
    api.post(`/courses/${courseId}/forum/${postId}/like`),
};

// VaultSphere API – stockage utilisateur (privilèges public/private/shared/vault)
export const vaultSphereAPI = {
  getItems: (params?: { section?: string; parentFolderId?: string | null; category?: string; subcategory?: string }) =>
    api.get('/vaultsphere/items', { params }),

  getCategories: () => api.get('/vaultsphere/categories'),

  createFolder: (data: { name: string; parentFolderId?: string | null; permission?: string; isInVault?: boolean; category?: string; subcategory?: string }) =>
    api.post('/vaultsphere/folders', data),

  updateFolder: (folderId: string, data: { name?: string; permission?: string; isInVault?: boolean; category?: string; subcategory?: string }) =>
    api.patch(`/vaultsphere/folders/${folderId}`, data),

  deleteFolder: (folderId: string) =>
    api.delete(`/vaultsphere/folders/${folderId}`),

  uploadFile: (formData: FormData) =>
    api.post('/vaultsphere/files', formData, {
      maxContentLength: 110 * 1024 * 1024,
      maxBodyLength: 110 * 1024 * 1024,
      timeout: 120000,
    }),

  downloadFile: (fileId: string) =>
    api.get(`/vaultsphere/files/${fileId}/download`, { responseType: 'blob' }),

  updateFile: (fileId: string, data: { name?: string; permission?: string; isInVault?: boolean }) =>
    api.patch(`/vaultsphere/files/${fileId}`, data),

  deleteFile: (fileId: string) =>
    api.delete(`/vaultsphere/files/${fileId}`),

  getFileSharedWith: (fileId: string) =>
    api.get(`/vaultsphere/files/${fileId}/shared-with`),

  getFileSharedDetails: (fileId: string) =>
    api.get(`/vaultsphere/files/${fileId}/shared-details`),

  shareFile: (fileId: string, payload: { userId?: string; teamId?: string; role?: 'viewer' | 'commenter' | 'editor'; sharedViaTeamId?: string }) =>
    api.post(`/vaultsphere/files/${fileId}/share`, { role: 'viewer', ...payload }),

  removeFileShare: (fileId: string, targetUserId: string) =>
    api.delete(`/vaultsphere/files/${fileId}/share/${targetUserId}`),

  removeFileShareTeam: (fileId: string, teamId: string) =>
    api.delete(`/vaultsphere/files/${fileId}/share/team/${teamId}`),

  getFolderSharedWith: (folderId: string) =>
    api.get(`/vaultsphere/folders/${folderId}/shared-with`),

  getFolderSharedDetails: (folderId: string) =>
    api.get(`/vaultsphere/folders/${folderId}/shared-details`),

  shareFolder: (folderId: string, payload: { userId?: string; teamId?: string; role?: 'viewer' | 'commenter' | 'editor'; sharedViaTeamId?: string }) =>
    api.post(`/vaultsphere/folders/${folderId}/share`, { role: 'viewer', ...payload }),

  removeFolderShare: (folderId: string, targetUserId: string) =>
    api.delete(`/vaultsphere/folders/${folderId}/share/${targetUserId}`),

  removeFolderShareTeam: (folderId: string, teamId: string) =>
    api.delete(`/vaultsphere/folders/${folderId}/share/team/${teamId}`),

  uploadMeetingRecording: (formData: FormData) =>
    api.post('/vaultsphere/meeting-recording', formData, {
      maxContentLength: 110 * 1024 * 1024,
      maxBodyLength: 110 * 1024 * 1024,
      timeout: 120000,
    }),

  vaultHasPin: () => api.get('/vaultsphere/vault/has-pin'),
  vaultSetPin: (pin: string) => api.post('/vaultsphere/vault/set-pin', { pin }),
  vaultVerifyPin: (pin: string) => api.post('/vaultsphere/vault/verify-pin', { pin }),
  vaultResetPin: (password: string, newPin: string) => api.post('/vaultsphere/vault/reset-pin', { password, newPin }),
};

export const teamsAPI = {
  getUserTeams: () => api.get('/teams/user-teams'),
  getTeamById: (id: string) => api.get(`/teams/${id}`),
};

export type ShareRole = 'viewer' | 'commenter' | 'editor';

export const usersSearchAPI = {
  /** scope: 'teams' = membres de mes équipes (collaboration), 'all' = tous les utilisateurs */
  search: (q: string, limit = 20, scope: 'teams' | 'all' = 'teams') =>
    api.get('/users/search', { params: { q: q.trim(), limit, scope } }),
};

// Health check
export const healthCheck = () => api.get('/health');

export default api;
